import { Environment, geoUtils, Scene, SkyParam } from "../base";
import { WebCadScene, MaterialManager, wcItemUtils } from "./index";
import * as THREE from "three";

/**
 * class: Lights
 */
export class Lights {
	private _lights: { [key: string]: THREE.Light[] };
	private _environment: Environment;
	constructor() {
		this._lights = {};
	}
	public get lights(): { [key: string]: THREE.Light[] } { return this._lights; }
	public get length(): number {
		let count = 0;
		for (let key in this._lights) {
            count += this._lights[key].length;
        }
		return count;
	}
	public get environment(): Environment { return this._environment; }
	public reset(except?: RegExp) {
		for (let key in this._lights) {
			if ((except != undefined) && (key.match(except))) {
				continue;
			}
			let ligs = this._lights[key];
			for (let i = 0; i < ligs.length; i++) {
				geoUtils.disposeObject(ligs[i]);
			}
			delete this._lights[key];
		}
	}

	public add(name: string, light: THREE.Light) {
		if (this._lights[name] == undefined) this._lights[name] = [];
		this._lights[name].push(light);
	}

	public addLights(scene: Scene): void {
		const wcScene = <WebCadScene>(scene);
		this._environment = wcScene.environment;
		this.add("sun", this._environment.sky.sun.me);
		this.add("ambient", this._environment.sky.ambient.me);
		const exLights = wcScene.exLights;
		for (let i = 0; i < exLights.length; i++) {
			this.add("exsun1", exLights[i].me);
		}
	}

	public getCurrent(): {} {
		let cur: any = {};
		for (let key in this._lights) {
			let ligs = this._lights[key];
			for (let i = 0; i < ligs.length; i++) {
				this.getCur(cur, ligs[i], key, i);
			}
		}
		cur['inclination'] = this.environment.sky.param.inclination;
		cur['azimuth'] = this.environment.sky.param.inclination;
		cur['night'] = this.environment.night ? 1 : 0;
		cur['turbidity'] = this.environment.sky.param.turbidity;
		cur['sunDiskIntensity'] = this.environment.sky.param.sunDiskIntensity;
		return cur;
	}

	private getCur(cur: any, light: THREE.Light, key: string, index: number): void {
		if (light instanceof THREE.DirectionalLight) {
			const dlight = <THREE.DirectionalLight>(light);
			cur[key + String(index) + "-intensity"] = dlight.intensity;
		}
		else if (light instanceof THREE.AmbientLight) {
			const alight = <THREE.AmbientLight>(light);
			cur[key + String(index) + "-intensity"] = alight.intensity;
		}
		else if (light instanceof THREE.PointLight) {
			const plight = <THREE.PointLight>(light);
			cur[key + String(index) + "-power"] = isNaN(plight.power) ? 0 : plight.power;
		}
		else if (light instanceof THREE.SpotLight) {
			const slight = <THREE.SpotLight>(light);
			cur[key + String(index) + "-power"] = isNaN(slight.power) ? 0 : slight.power;
		}
	}

	public getGoal(matmgr: MaterialManager, night: boolean): {} {
		let goal: any = {};
		for (let key in this._lights) {
			let ligs = this._lights[key];
			for (let i = 0; i < ligs.length; i++) {
				this.getG(goal, ligs[i], key, i, matmgr, night);
			}
		}
		goal['inclination'] = night ? 1.0 : 0.8;
		goal['azimuth'] = night ? 0.5 : 0.8;
		goal['night'] = night ? 1 : 0;
		goal['turbidity'] = night ? 0.1 : 1.0;
		goal['sunDiskIntensity'] = night ? 0 : 1.0;
		return goal;
	}

	private getG(goal: any, light: THREE.Light, key: string, index: number, matmgr: MaterialManager, night: boolean): void {
		if (light instanceof THREE.DirectionalLight) {
			let pref: any = matmgr.lightPref(key);
			let dlight = <THREE.DirectionalLight>(light);
			if (night) goal[key + String(index) + "-intensity"] = 0.1;
			else goal[key + String(index) + "-intensity"] = (pref == undefined) ? dlight.intensity : Number(pref['intensity']);
		}
		else if (light instanceof THREE.AmbientLight) {
			let pref: any = matmgr.lightPref(key);
			let alight = <THREE.AmbientLight>(light);
			if (night) goal[key + String(index) + "-intensity"] = 0.1;
			else goal[key + String(index) + "-intensity"] = (pref == undefined) ? alight.intensity : Number(pref['intensity']);
		}
		else if (light instanceof THREE.PointLight) {
			let pref: any = matmgr.lightPref(wcItemUtils.getObjectId(light));
			let plight = <THREE.PointLight>(light);
			if (night) goal[key + String(index) + "-power"] = (pref == undefined) ? plight.power : Number(pref['power']);
			else goal[key + String(index) + "-power"] = 0;

			if (pref != undefined) {
				plight.decay = Number(pref['decay']);
				plight.distance = Number(pref['distance']);
				plight.color = new THREE.Color('#' + pref['color'].split('x')[1]);
			}
		}
		else if (light instanceof THREE.SpotLight) {
			let pref: any = matmgr.lightPref(wcItemUtils.getObjectId(light));
			let slight = <THREE.SpotLight>(light);
			if (night) goal[key + String(index) + "-power"] = (pref == undefined) ? slight.power : Number(pref['power']);
			else goal[key + String(index) + "-power"] = 0;

			if (pref != undefined) {
				slight.decay = pref['decay'];
				slight.distance = Number(pref['distance']);
				slight.color = new THREE.Color('#' + pref['color'].split('x')[1]);
			}
		}
	}

	public apply(current: any): void {
		for (let key in this._lights) {
			const ligs = this._lights[key];
			for (let i = 0; i < ligs.length; i++) {
				this.applyOne(current, ligs[i], key, i);
			}
		}
		let param = new SkyParam;
		param.inclination = current['inclination'];
		param.azimuth = current['azimuth'];
		param.turbidity = current['turbidity'];
		param.sunDiskIntensity = current['sunDiskIntensity'];
		this.environment.sky.param = param;

		let night = (current['night'] > 0.90);
		if (night != this.environment.night) this.environment.night = night;
	}

	private applyOne(current: any, light: THREE.Light, key: string, index: number): void {
		if (light instanceof THREE.DirectionalLight) {
			let value = current[key + String(index) + "-intensity"];
			if (value != undefined) {
				let dlight = <THREE.DirectionalLight>(light);
				dlight.intensity = value;
			}
		}
		else if (light instanceof THREE.AmbientLight) {
			let value = current[key + String(index) + "-intensity"];
			if (value != undefined) {
				let alight = <THREE.AmbientLight>(light);
				alight.intensity = value;
			}
		}
	}
}
